import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface FiscalYearOption {
  fiscal_year: number;
  // Never both on the same entry - a page offers either quarter or month granularity, never both
  // (services/analysis/service.py's own comment on this).
  quarters?: string[];
  months?: string[];
}

interface Props {
  options: FiscalYearOption[];
  onChange: (key: string, value: string) => void;
  testId: string;
  // The committed selection, comma-joined, when the parent owns it - needed once this picker can be
  // hidden and re-shown (ApplyFiltersTile.tsx only shows it while date_range is "custom"), which
  // would otherwise lose its own internal state on unmount. Uncontrolled when omitted.
  value?: string;
  // The filter key picked periods are written to - "date_range" when this picker is the page's only
  // date control, "fiscal_year" when a date_range dropdown sits alongside it (AnalyticsPage.tsx's
  // buildRequestFilters folds that back into date_range on submission).
  valueKey?: string;
}

// Every period this component submits/reads is one "{period} FY {year}" label (e.g.
// "Q1 FY 2026"), matching the real Brand Performance dashboard's own MultiSelectFilter checkValue
// convention (`${value} ${parentValue}`) exactly - kept as one helper so building it never drifts.
export const periodLabel = (period: string, fiscalYear: number) => `${period} FY ${fiscalYear}`;

// The backend sends this as a nested {fiscal_year, quarters | months} array - never a flat option
// list like every other field (services/analysis/service.py's own comment on this synthetic
// field) - so it needs its own two-level Year -> Quarter/Month picker rather than the generic
// <select>. Users can pick more than one quarter/month, across more than one fiscal year, at
// once (matching the real Brand Performance dashboard's own "DATE RANGE" control, which is
// likewise a genuine multi-select, not a single year+period pair) - so this reuses the same
// checkbox-popover pattern/plumbing as MultiSelectFilterField.tsx (portal, position, backdrop,
// scroll-close, flip-upward - see that component's own comments for the full reasoning on each),
// just with a two-level grouped list instead of a flat one, and no "All" master checkbox (there's
// no real "every period ever offered" concept for a fiscal-year picker the way there is for a
// plain market/model list).
//
// Submission matches the REAL Brand Performance dashboard's own "DATE RANGE" control exactly
// (BrandPerformanceFilters.tsx's MultiSelectFilter, childKey "quarter"/"month") rather than
// resolving anything into concrete dates client-side: every picked period submits its own literal
// label - "{period} FY {year}", e.g. "Q1 FY 2026" or "April FY 2026" - comma-joined for more than
// one, as `date_range` itself (AnalyticsPage.tsx's own buildRequestFilters/FISCAL_YEAR_LABEL check
// already expects and re-splits exactly this shape). BrandPerformanceFilters.date_range: Union[
// InternalTimespan, List[str], str] already accepts exactly this shape - the same Pydantic schema
// the Downloads page's own generic /analysis/{id}/filters endpoint reuses unchanged, so this works
// identically here. An earlier version of this component instead computed start_date/end_date and
// submitted date_range="custom" - a plausible-looking contract that turned out to have no actual
// precedent or support anywhere in the real dashboard's own live code path.
export const FiscalYearFilterField = (props: Props) => {
  const { options, onChange, testId, value, valueKey = "date_range" } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<string[]>([]);
  // The last committed selection - re-seeds the popover's own draft each time it reopens, and
  // drives the trigger button's own summary label.
  const [uncontrolledCommitted, setCommitted] = useState<string[]>([]);
  const committed = value !== undefined ? (value ? value.split(",") : []) : uncontrolledCommitted;
  // Where the portaled popover renders, in viewport coordinates - see MultiSelectFilterField.tsx's
  // own identical comment for why (not a descendant of .analysis_tile any more, so can't just be
  // `position: absolute` relative to it).
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Closes on any outside scroll (excluding a scroll inside the popover's own options list) - same
  // behavior/reasoning as MultiSelectFilterField.tsx's own identical effect.
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  // Flips the popover upward when there's no room below - same behavior/reasoning as
  // MultiSelectFilterField.tsx's own identical effect.
  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current || !triggerRef.current) return;
    const popoverHeight = popoverRef.current.getBoundingClientRect().height;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    if (spaceBelow < popoverHeight + 4 && spaceAbove >= popoverHeight + 4) {
      setPopoverPosition((prev) => ({ ...prev, top: triggerRect.top - popoverHeight - 4 }));
    }
  }, [isOpen]);

  const open = () => {
    setDraftSelected(committed);
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsOpen(true);
  };

  const togglePeriod = (period: string, fiscalYear: number) => {
    const label = periodLabel(period, fiscalYear);
    setDraftSelected((prev) => (prev.includes(label) ? prev.filter((value) => value !== label) : [...prev, label]));
  };

  const commit = () => {
    setCommitted(draftSelected);
    onChange(valueKey, draftSelected.join(","));
    setIsOpen(false);
  };

  const triggerLabel =
    committed.length === 0 ? "Select fiscal periods" : committed.length === 1 ? committed[0] : `${committed.length} selected`;

  return (
    <div className="analysis_multiselect">
      <button
        ref={triggerRef}
        type="button"
        className="analysis_multiselect_trigger"
        onClick={open}
        data-test-id={testId}
        aria-expanded={isOpen}
      >
        {triggerLabel}
        <span className="analysis_multiselect_arrow" />
      </button>

      {/* Portaled to <body> for the same reason as MultiSelectFilterField.tsx's own identical
          popover - escapes .analysis_tile's own `overflow: hidden` clip rather than just
          scrolling within it. */}
      {isOpen &&
        createPortal(
          <>
            <div className="analysis_multiselect_backdrop" onClick={() => setIsOpen(false)} data-test-id={`${testId}-backdrop`} />
            <div
              ref={popoverRef}
              className="analysis_multiselect_popover"
              style={{ top: popoverPosition.top, left: popoverPosition.left, width: popoverPosition.width }}
              data-test-id={`${testId}-popover`}
            >
              <div className="analysis_multiselect_options">
                {options.map((yearOption) => {
                  const periods = yearOption.months ?? yearOption.quarters ?? [];
                  return (
                    <div className="analysis_fiscal_year_group" key={yearOption.fiscal_year}>
                      <p className="analysis_fiscal_year_group_label">FY{yearOption.fiscal_year}</p>
                      {periods.map((period) => {
                        const label = periodLabel(period, yearOption.fiscal_year);
                        return (
                          <label className="analysis_multiselect_option" key={label}>
                            <input
                              type="checkbox"
                              checked={draftSelected.includes(label)}
                              onChange={() => togglePeriod(period, yearOption.fiscal_year)}
                              data-test-id={`${testId}-option-${label}`}
                            />
                            {period}
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <button type="button" className="analysis_multiselect_done" onClick={commit} data-test-id={`${testId}-done`}>
                Done
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
